const integerFormatter = new Intl.NumberFormat("uk-UA", {
  maximumFractionDigits: 0,
});

const decimalFormatter = new Intl.NumberFormat("uk-UA", {
  maximumFractionDigits: 4,
});

const firstDefined = (...values) =>
  values.find((value) => value !== undefined && value !== null && value !== "");

const toOptionalInteger = (...values) => {
  const value = firstDefined(...values);
  if (value === undefined) return null;

  const number = Number(value);
  if (!Number.isFinite(number)) return null;
  return Math.max(0, Math.trunc(number));
};

const toOptionalNumber = (...values) => {
  const value = firstDefined(...values);
  if (value === undefined) return null;

  const number = Number(value);
  return Number.isFinite(number) && number >= 0 ? number : null;
};

const toBoolean = (value, fallback = false) =>
  typeof value === "boolean" ? value : fallback;

const unwrapPayload = (payload) => payload?.data ?? payload ?? {};

export const toNonNegativeInteger = (value) => {
  const number = Number(value);
  if (!Number.isFinite(number)) return 0;
  return Math.max(0, Math.trunc(number));
};

export const formatKopecks = (value, { signed = false } = {}) => {
  const kopecks = Number(value);
  if (!Number.isFinite(kopecks)) return "0,00 грн";

  const integerKopecks = Math.trunc(kopecks);
  const absolute = Math.abs(integerKopecks);
  const hryvnias = Math.floor(absolute / 100);
  const cents = String(absolute % 100).padStart(2, "0");
  const sign = integerKopecks < 0 ? "−" : signed && integerKopecks > 0 ? "+" : "";

  return `${sign}${integerFormatter.format(hryvnias)},${cents} грн`;
};

export const kopecksToHryvniaNumber = (value, fallback = 0) => {
  const kopecks = Number(value);
  if (!Number.isFinite(kopecks)) return Number(fallback) || 0;
  return Number((Math.trunc(kopecks) / 100).toFixed(2));
};
export const kopecksToHryvniaInput = (value) => {
  const kopecks = toNonNegativeInteger(value);
  return String(Math.floor(kopecks / 100)) + "." + String(kopecks % 100).padStart(2, "0");
};

export const hryvniaInputToKopecks = (value) => {
  const normalized = String(value ?? "").trim().replace(/\s+/g, "").replace(",", ".");
  if (!/^\d*(?:\.\d{0,2})?$/.test(normalized)) return null;
  if (!normalized || normalized === ".") return 0;
  const [wholePart = "0", fractionPart = ""] = normalized.split(".");
  const whole = Number(wholePart || 0);
  const fraction = Number(fractionPart.padEnd(2, "0") || 0);
  if (!Number.isSafeInteger(whole) || whole < 0 || whole > 90071992547409) return null;
  const kopecks = whole * 100 + fraction;
  return Number.isSafeInteger(kopecks) ? kopecks : null;
};

const normalizeRule = (rule = {}, legacy = {}) => {
  const enabled = toBoolean(
    firstDefined(rule.enabled, legacy.enabled),
    false,
  );
  const directRatePercent =
    toOptionalNumber(
      rule.ratePercent,
      rule.percent,
      rule.rate,
      legacy.ratePercent,
      legacy.rate,
    );
  const rateBps = toOptionalNumber(
    rule.rateBps,
    legacy.rateBps,
  );
  const ratePercent =
    directRatePercent ?? (rateBps !== null ? rateBps / 100 : 0);
  return { enabled, ratePercent };
};

export const normalizeLoyaltySettings = (payload) => {
  const root = unwrapPayload(payload);
  const source = root.settings ?? root.loyaltySettings ?? root;
  const regularSource =
    source.regularProducts ?? source.nonSaleProducts ?? source.regular ?? {};
  const saleSource =
    source.saleProducts ?? source.discountedProducts ?? source.sale ?? {};
  const mode = source.mode || (source.enabled ? "active" : "legacy_disabled");

  return {
    mode,
    visible: mode !== "hidden",
    earningEnabled: mode === "active" || mode === "maintenance",
    redemptionEnabled: mode === "active",
    enabled: mode === "active" || mode === "maintenance",
    revision: source.revision ?? null,
    regularProducts: normalizeRule(regularSource, {
      enabled: source.regularProductsEnabled,
      rate: source.regularProductsRate ?? source.regularRate,
      rateBps: source.regularProductsRateBps ?? source.regularRateBps,
    }),
    saleProducts: normalizeRule(saleSource, {
      enabled: source.saleProductsEnabled,
      rate: source.saleProductsRate ?? source.saleRate,
      rateBps: source.saleProductsRateBps ?? source.saleRateBps,
    }),
    maxRedeemPercent:
      toOptionalNumber(source.maxRedeemPercent, source.redeemLimitPercent) ??
      ((toOptionalNumber(source.maxRedeemBps) ?? 0) / 100),
    expirationMonths:
      toOptionalInteger(source.expirationMonths, source.validityMonths) ?? 0,
    defaultMultiplier:
      toOptionalNumber(source.defaultMultiplier, source.multiplier) ??
      (toOptionalNumber(source.defaultMultiplierBps) !== null
        ? toOptionalNumber(source.defaultMultiplierBps) / 10000
        : 1),
    updatedAt: source.updatedAt ?? null,
  };
};

const formatRate = (value) => decimalFormatter.format(Number(value) || 0);
export const LOYALTY_MAINTENANCE_MESSAGE = "У програмі лояльності тривають регламентні роботи. Використання бонусів для оплати тимчасово недоступне. Бонуси за покупки продовжують нараховуватися авторизованим користувачам.";

export const buildLoyaltyExplanation = (settingsPayload) => {
  const settings = normalizeLoyaltySettings(settingsPayload);
  if (!settings.visible) return "";
  if (settings.mode === "maintenance") return LOYALTY_MAINTENANCE_MESSAGE;
  if (!settings.enabled) return "Програма лояльності наразі неактивна.";

  const rules = [
    settings.regularProducts.enabled
      ? formatRate(settings.regularProducts.ratePercent) +
        "% бонусами за товари за звичайною ціною"
      : "за товари за звичайною ціною бонуси не нараховуються",
    settings.saleProducts.enabled
      ? formatRate(settings.saleProducts.ratePercent) +
        "% бонусами за акційні товари"
      : "за акційні товари бонуси не нараховуються",
  ];

  const multiplier =
    settings.defaultMultiplier !== 1
      ? ` Поточний коефіцієнт нарахування: ×${formatRate(
          settings.defaultMultiplier,
        )}.`
      : "";

  return "Отримуйте " + rules.join(" та ") + ". Бонуси можна використати як знижку на наступні покупки." + multiplier;
};

const normalizeQuoteItem = (item = {}, index = 0) => ({
  ...item,
  key: String(item._id ?? item.id ?? item.productId ?? item.productCode ?? index),
  productId: item.productId ?? item._id ?? item.id ?? null,
  quantity: toOptionalInteger(item.quantity, item.qty) ?? 0,
  isSale: Boolean(item.isSale ?? item.sale ?? item.isDiscounted),
  regularLineKopecks: toOptionalInteger(
    item.regularLineKopecks,
    item.originalLineKopecks,
    item.listLineKopecks,
  ),
  paidMoneyKopecks: toOptionalInteger(
    item.paidMoneyKopecks,
    item.lineSubtotalKopecks,
    item.itemsSubtotalKopecks,
    item.lineTotalKopecks,
  ),
  discountKopecks: toOptionalInteger(
    item.discountKopecks,
    item.productDiscountKopecks,
    item.lineDiscountKopecks,
  ),
  earnedBonusKopecks: toOptionalInteger(item.earnedBonusKopecks, item.estimatedEarnBonusKopecks) ?? 0,
});

export const normalizeLoyaltyQuote = (payload) => {
  const root = unwrapPayload(payload);
  const source = root.quote ?? root.loyaltyQuote ?? root;
  const itemsSource =
    source.items ??
    source.itemBreakdown ??
    source.breakdown ??
    source.lineItems ??
    [];
  const appliedBonusKopecks =
    toOptionalInteger(
      source.appliedBonusKopecks,
      source.redeemedBonusKopecks,
      source.bonusKopecksToRedeem,
    ) ?? 0;
  const productDiscountKopecks = toOptionalInteger(
    source.productDiscountKopecks,
    source.discountsKopecks,
    source.totalDiscountKopecks,
  );
  const itemsSubtotalKopecks = toOptionalInteger(
    source.itemsSubtotalKopecks,
    source.merchandiseSubtotalKopecks,
    source.goodsSubtotalKopecks,
    source.subtotalKopecks,
  );
  const productsBeforeDiscountKopecks = toOptionalInteger(
    source.productsBeforeDiscountKopecks,
    source.regularSubtotalKopecks,
    source.originalItemsSubtotalKopecks,
  );
  const payableItemsKopecks = toOptionalInteger(
    source.payableItemsKopecks,
    source.itemsPayableKopecks,
    source.finalItemsKopecks,
    source.payableKopecks,
    source.totalKopecks,
  );

  return {
    quoteId: source.quoteId ?? source.id ?? null,
    settings: normalizeLoyaltySettings(source.settings ?? root.settings ?? {}),
    registered:
      typeof source.registered === "boolean"
        ? source.registered
        : typeof source.eligible === "boolean"
          ? source.eligible
          : null,
    availableBonusKopecks:
      toOptionalInteger(
        source.availableBonusKopecks,
        source.balanceBonusKopecks,
        source.activeBonusKopecks,
        source.balance,
      ) ?? 0,
    maxRedeemableBonusKopecks:
      toOptionalInteger(
        source.maxRedeemableBonusKopecks,
        source.maxBonusKopecksToRedeem,
        source.redeemableBonusKopecks,
      ) ?? 0,
    appliedBonusKopecks,
    bonusDiscountKopecks:
      toOptionalInteger(
        source.bonusDiscountKopecks,
        source.loyaltyDiscountMinor,
        source.bonusKopecksDiscountKopecks,
      ) ?? appliedBonusKopecks,
    productsBeforeDiscountKopecks:
      productsBeforeDiscountKopecks ??
      (itemsSubtotalKopecks !== null && productDiscountKopecks !== null
        ? itemsSubtotalKopecks + productDiscountKopecks
        : itemsSubtotalKopecks),
    productDiscountKopecks,
    itemsSubtotalKopecks,
    payableItemsKopecks,
    estimatedEarnBonusKopecks:
      toOptionalInteger(
        source.estimatedEarnBonusKopecks,
        source.earnBonusKopecks,
        source.pendingEarnBonusKopecks,
      ) ?? 0,
    items: Array.isArray(itemsSource)
      ? itemsSource.map(normalizeQuoteItem)
      : [],
    hasAuthoritativeTotals:
      itemsSubtotalKopecks !== null && payableItemsKopecks !== null,
  };
};

export const normalizeLoyaltySummary = (payload) => {
  const root = unwrapPayload(payload);
  const source = root.summary ?? root.loyalty ?? root;

  return {
    registered:
      typeof source.registered === "boolean" ? source.registered : true,
    availableBonusKopecks:
      toOptionalInteger(
        source.availableBonusKopecks,
        source.activeBonusKopecks,
        source.balanceBonusKopecks,
        source.balance,
      ) ?? 0,
    pendingBonusKopecks:
      toOptionalInteger(source.pendingBonusKopecks, source.pendingBalance) ?? 0,
    redeemedBonusKopecks:
      toOptionalInteger(
        source.redeemedBonusKopecks,
        source.usedBonusKopecks,
        source.totalRedeemedBonusKopecks,
        source.lifetimeRedeemedBonusKopecks,
      ) ?? 0,
    expiredBonusKopecks:
      toOptionalInteger(
        source.expiredBonusKopecks,
        source.totalExpiredBonusKopecks,
        source.lifetimeExpiredBonusKopecks,
      ) ?? 0,
    expiresAt:
      source.expiresAt ?? source.expirationDate ?? source.nextExpirationAt ?? null,
    settings: normalizeLoyaltySettings(source.settings ?? root.settings ?? {}),
  };
};

const DEBIT_TRANSACTION_TYPES = new Set([
  "redeem",
  "cancel",
  "expire",
  "manual_debit",
]);

export const normalizeLoyaltyTransaction = (transaction = {}, index = 0) => {
  const type = String(
    transaction.type ?? transaction.operationType ?? transaction.operation ?? "",
  ).toLowerCase();
  const rawBonusKopecks = Number(
    firstDefined(
      transaction.amountKopecks,
      transaction.amount,
      0,
    ),
  );
  const absoluteBonusKopecks = Math.abs(Number.isFinite(rawBonusKopecks) ? Math.trunc(rawBonusKopecks) : 0);
  const signedBonusKopecks =
    rawBonusKopecks < 0 || DEBIT_TRANSACTION_TYPES.has(type)
      ? -absoluteBonusKopecks
      : absoluteBonusKopecks;

  return {
    ...transaction,
    id: transaction._id ?? transaction.id ?? `loyalty-${index}`,
    type,
    status: String(transaction.status ?? "").toLowerCase(),
    amountKopecks: signedBonusKopecks,
    orderNumber:
      transaction.orderNumber ??
      transaction.order?.orderNumber ??
      transaction.metadata?.orderNumber ??
      "",
    reason: transaction.reason ?? transaction.description ?? "",
    createdAt:
      transaction.createdAt ?? transaction.date ?? transaction.activatedAt ?? null,
    expiresAt: transaction.expiresAt ?? null,
  };
};

export const normalizeLoyaltyTransactions = (payload, fallbackPage = 1) => {
  const root = unwrapPayload(payload);
  const items = Array.isArray(root)
    ? root
    : root.transactions ?? root.items ?? root.results ?? [];
  const pagination = root.pagination ?? root.meta ?? {};
  const page =
    toOptionalInteger(pagination.page, root.page, fallbackPage) || fallbackPage;
  const totalPages =
    toOptionalInteger(
      pagination.totalPages,
      root.totalPages,
      pagination.pages,
    ) ?? 1;

  return {
    items: Array.isArray(items)
      ? items.map(normalizeLoyaltyTransaction)
      : [],
    page,
    totalPages: Math.max(1, totalPages),
    total:
      toOptionalInteger(pagination.total, root.total, root.totalCount) ??
      (Array.isArray(items) ? items.length : 0),
  };
};

export const buildLoyaltyCartItems = (cartItems = []) =>
  (Array.isArray(cartItems) ? cartItems : [])
    .map((item) => ({
      productId: item?.productId ?? item?._id ?? null,
      quantity: toNonNegativeInteger(item?.quantity),
    }))
    .filter((item) => item.productId && item.quantity > 0);

export const getCustomerRedeemBonusKopecks = ({
  customer,
  quote,
  fallbackBonusKopecks = 0,
} = {}) => {
  if (!customer || quote?.registered === false) return 0;
  return toNonNegativeInteger(quote?.appliedBonusKopecks ?? fallbackBonusKopecks);
};

export const buildLoyaltyOrderFields = ({
  customer,
  quote,
  fallbackBonusKopecks = 0,
} = {}) => {
  const fields = {
    bonusKopecksToRedeem: getCustomerRedeemBonusKopecks({
      customer,
      quote,
      fallbackBonusKopecks,
    }),
  };

  if (quote?.quoteId) fields.loyaltyQuoteId = quote.quoteId;
  if (quote?.hasAuthoritativeTotals) fields.expectedItemsPayableKopecks = quote.payableItemsKopecks;
  return fields;
};

export const LOYALTY_OPERATION_LABELS = Object.freeze({
  earn: "Нарахування",
  redeem: "Списання",
  refund: "Повернення",
  cancel: "Анульовано",
  expire: "Прострочено",
  manual_adjustment: "Ручне коригування",
  manual_credit: "Ручне нарахування",
  manual_debit: "Ручне списання",
});

export const LOYALTY_OPERATION_COMMENTS = Object.freeze({
  earn: "Нарахування бонусів за покупку",
  redeem: "Списання бонусів для оплати замовлення",
});

export const LOYALTY_STATUS_LABELS = Object.freeze({
  pending: "Очікують",
  active: "Активні",
  completed: "Завершено",
  cancelled: "Скасовано",
  canceled: "Скасовано",
  expired: "Прострочено",
});

export const formatLoyaltyDate = (value, { includeTime = true } = {}) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";

  const options = {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  };

  if (includeTime) {
    options.hour = "2-digit";
    options.minute = "2-digit";
  }

  return date.toLocaleString("uk-UA", options);
};
